import { useMutation, useQuery } from "../state"
import { certificateFactory, CertificateType, ChallengeType, KeyLength, KeyType } from "./models/Certificate"
import { customerFactory } from "../common/models/Customer"
import { deploymentFactory } from "./models/Deployment"
import { domainFactory } from "./models/Domain"
import { serverFactory } from "./models/Server"
import { Site, siteFactory } from "./models/Site"
import { PHPConfig, phpConfigFactory } from "./models/windhosting/PHPConfig"
import { phpInfoFactory } from "./models/windhosting/PHPInfo"
import { SiteConfig, siteConfigFactory } from "./models/windhosting/SiteConfig"
import { siteInfoFactory } from "./models/windhosting/siteInfo"
import { WebserverConfig, webserverConfigFactory } from "./models/windhosting/WebserverConfig"
import { webserverInfoFactory } from "./models/windhosting/WebserverInfo"

const staleTime = 60 * 1000

// Sites
export const useSites = () => useQuery(
  ['sites', 'list'],
  async ({ http }) => siteFactory.fromItemsResponse(await http.get("sites")),
  { staleTime }
)

export const useSite = (site: string) => useQuery(
  ['sites', site],
  async ({ http }) => siteFactory.fromItemResponse(await http.get(`sites/${site}`)),
  { staleTime }
)

export const useUpdateSite = (site: string) => useMutation(
  (vars: Site, { http }) => http.put(`sites/${site}`, vars),
  { onSuccess: ({ client }) => client.invalidate(['sites']) }
)

export const useSshKeyAdd = (site: string) => useMutation(
  async (vars: string, { http }) => {
    const json = await http.post(`sites/${site}/authorized_keys`, { authorized_key: vars })
    return (json && typeof json === 'object' && 'item' in json) ? deploymentFactory.fromItemResponse(json) : null
  },
  { onSuccess: ({ client }) => client.invalidate(['sites', site]) }
)

export const useSshKeyDel = (site: string) => useMutation(
  async (vars: string, { http }) => {
    const json = await http.delete(`sites/${site}/authorized_keys/${vars}`)
    return (json && typeof json === 'object' && 'item' in json) ? deploymentFactory.fromItemResponse(json) : null
  },
  { onSuccess: ({ client }) => client.invalidate(['sites', site]) }
)

// Certificates
export const useCertificates = () => useQuery(
  ['certificates', 'list'],
  async ({ http }) => certificateFactory.fromItemsResponse(await http.get('certificates')),
  { staleTime }
)

export const useCertificate = (certificate: string) => useQuery(
  ['certificates', certificate],
  async ({ http }) => certificateFactory.fromItemResponse(await http.get(`certificates/${certificate}`)),
  { staleTime }
)

export interface CertificateOrder {
  type: CertificateType,
  customer: string,
  server: string
  displayName: string,
  domains: string[],
  challengeType: ChallengeType,
  keyType: KeyType,
  keyLength: KeyLength,
}

export const useOrderCertificate = () => useMutation(
  async (vars: CertificateOrder, { http }) => certificateFactory.fromItemResponse(await http.post(`certificates/order`, {
    type: vars.type,
    customer_uuid: vars.customer,
    server_uuid: vars.server,
    display_name: vars.displayName,
    domains: vars.domains,
    challenge_type: vars.challengeType,
    key_type: vars.keyType,
    key_length: vars.keyLength,
  })),
  {
    onSuccess: ({ client }) => client.invalidate(['certificates'])
  }
)

export const useFinalizeCertificate = () => useMutation(
  async (vars: string, { http }) => deploymentFactory.fromItemResponse(await http.post(`certificates/${vars}/finalize`, {})),
  {
    onSuccess: ({ client }) => client.invalidate(['certificates'])
  }
)

export const useDeleteCertificate = (certificate: string) => useMutation(
  async (_, { http }) => deploymentFactory.fromItemResponse(await http.delete(`certificates/${certificate}`)),
  { onSuccess: ({ client }) => client.invalidate(['certificates']) }
)

// Servers
export const useServers = () => useQuery(
  ['servers', 'list'],
  async ({ http }) => serverFactory.fromItemsResponse(await http.get("servers")),
  { staleTime }
)

export const useServer = (server: string) => useQuery(
  ['servers', server],
  async ({ http }) => serverFactory.fromItemResponse(await http.get(`servers/${server}`)),
  { staleTime }
)

// Customers
export const useCustomers = () => useQuery(
  ['customers', 'list'],
  async ({ http }) => customerFactory.fromItemsResponse(await http.get("customers")),
  { staleTime }
)

// Deployments
export const useEntityDeployments = (entity: string) => useQuery(
  ['deployments', 'entity', entity],
  async ({ http }) => deploymentFactory.fromItemsResponse(await http.get(`deployments?entity=${entity}`)),
)

// Domains
export const useDomains = () => useQuery(
  ['domains'],
  async ({ http }) => domainFactory.fromItemsResponse(await http.get('domains')),
  { staleTime }
)

// Windhosting
export const useWindhostingPHPInfo = (server: string) => useQuery(
  ['windhosting', 'info', server, 'php'],
  async ({ http }) => phpInfoFactory.fromItemResponse(await http.get(`windhosting/info/${server}/php`)),
  { staleTime }
)

export const useWindhostingPHP = (server: string, version: string) => useQuery(
  ['windhosting', server, 'php', version],
  async ({ http }) => phpConfigFactory.fromItemResponse(await http.get(`windhosting/${server}/php/${version}`)),
)

export const useUpdateWindhostingPHP = (server: string, version: string) => useMutation(
  async (vars: PHPConfig, { http }) => deploymentFactory.fromItemResponse(await http.put(`windhosting/${server}/php/${version}`, {
    ini: vars.ini,
    default_pool_config: vars.defaultPoolConfig,
    pools: vars.pools,
  })),
)

export const useWindhostingWebserverInfo = (server: string) => useQuery(
  ['windhosting', 'info', server, 'webserver'],
  async ({ http }) => webserverInfoFactory.fromItemResponse(await http.get(`windhosting/info/${server}/webserver`)),
  { staleTime }
)

export const useWindhostingWebserver = (server: string) => useQuery(
  ['windhosting', server, 'webserver'],
  async ({ http }) => webserverConfigFactory.fromItemResponse(await http.get(`windhosting/${server}/webserver`)),
)

export const useUpdateWindhostingWebserver = (server: string) => useMutation(
  async (vars: WebserverConfig, { http }) => deploymentFactory.fromItemResponse(await http.put(`windhosting/${server}/webserver`, vars)),
)

export const useWindhostingSiteInfo = (server: string) => useQuery(
  ['windhosting', 'info', server, 'site'],
  async ({ http }) => siteInfoFactory.fromItemResponse(await http.get(`windhosting/info/${server}/site`)),
  { staleTime }
)

export const useWindhostingSite = (server: string, site: string) => useQuery(
  ['windhosting', server, 'site', site],
  async ({ http }) => siteConfigFactory.fromItemResponse(await http.get(`windhosting/${server}/site/${site}`)),
  { staleTime }
)

/** The PHP versions a site can use. Unlike useWindhostingPHPInfo this only needs access to the site, not the server. */
export const useWindhostingSitePHPInfo = (server: string, site: string) => useQuery(
  ['windhosting', server, 'site', site, 'php'],
  async ({ http }) => phpInfoFactory.fromItemResponse(await http.get(`windhosting/${server}/site/${site}/php`)),
  { staleTime }
)

export const useWindhostingSiteWebserverInfo = (server: string, site: string) => useQuery(
  ['windhosting', server, 'site', site, 'webserver'],
  async ({ http }) => webserverInfoFactory.fromItemResponse(await http.get(`windhosting/${server}/site/${site}/webserver`)),
  { staleTime }
)

export const useUpdateWindhostingSite = (server: string, site: string) => useMutation(
  async (vars: SiteConfig, { http }) => deploymentFactory.fromItemResponse(await http.put(`windhosting/${server}/site/${site}`, {
    certificate: vars.certificate,
    php_version: vars.phpVersion,
    php_pool_config: vars.phpPoolConfig,
    webserver_config: vars.webserverConfig,
    alias_domains: vars.aliasDomains,
  })),
)
